import * as yup from "yup";

import { FeatureQueryStatus, FeatureType } from "src/clients/features-control";

export type ColumnExistsValidationContext = {
  availableColumns: string[];
  values?: {
    columns?: {
      features?: Array<{ feature_key?: string | null }>;
    };
  };
};

export const columnExistsValidation = {
  name: "column-exists",
  message: "Column name doesn't exist in query results. Update the value.",
  test: (
    value: string | null | undefined,
    context: yup.TestContext<yup.AnyObject>,
  ) => {
    if (!value) return true;
    const availableColumns = context.options.context?.availableColumns;
    // Skip validation when no columns are available (e.g. empty result set)
    if (!availableColumns?.length) return true;
    return availableColumns.includes(value);
  },
} as const;

export const schema = yup.object({
  query: yup.string().required().min(1),
  columns: yup
    .object({
      entities: yup
        .array()
        .of(
          yup.object().shape({
            column_name: yup
              .string()
              .required("Entity column is required. Run the query to map it.")
              .test(columnExistsValidation),
            entity_name: yup.string().required("Entity is required"),
          }),
        )
        .length(1),
      features: yup
        .array()
        .of(
          yup.object().shape({
            column_name: yup
              .string()
              .transform((value) => value?.trim())
              .required("Column name is required")
              .test(columnExistsValidation),
            feature_key: yup
              .string()
              .transform((value) => value?.trim())
              .required("Feature key is required")
              .test(
                "unique-key",
                "Feature key must be unique",
                function (value) {
                  if (!value) return true;
                  const features =
                    this.options.context?.values?.columns?.features ?? [];
                  const count = features.filter(
                    (f: { feature_key?: string | null }) =>
                      f?.feature_key?.trim() === value,
                  ).length;
                  return count <= 1;
                },
              ),
            name: yup
              .string()
              .transform((value) => value?.trim())
              .required("Feature name is required"),
            feature_type: yup.mixed<FeatureType>().optional(),
            feature_default: yup
              .string()
              .nullable()
              .transform((value) => value ?? "")
              .required(
                "Default value is required, set None if not applicable",
              ),
            description: yup.string().optional(),
          }),
        )
        .min(1),
      grouping_keys: yup
        .array()
        .of(yup.string().required().test(columnExistsValidation))
        .max(1),
    })
    .required(),
  window_length: yup.number().required(),
  window_hop: yup.number().required(),
  end_datetime: yup.date().optional(),
});

export type QueryFormValues = yup.InferType<typeof schema>;

export const defaultFormValues = {
  status: FeatureQueryStatus.INACTIVE,
  query: "",
  columns: {
    entities: [{ column_name: "", entity_name: "" }],
    grouping_keys: [],
    features: [],
  },
  window_length: undefined,
  window_hop: undefined,
};
